import type {OttopilotAdditionalButtonFormUrlQueryParam} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import {OttopilotAdditionalButtonKind} from "@vesta/vos-types/modules/workspaceLevel/workspaceCapabilities/ottopilotEventsWorkspaceCapability/domain/OttopilotEventAdditionalButtonsConfig";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {uuidZodValidator} from "@vesta/global-common-typescript/validators/zodValidators";
import {
    findFormPageByFormId,
    isFormPage,
    type Form,
    type FormPage,
    type WorkspaceConfig,
} from "@vesta/vos-types/modules/workspaceLevel/workspaceUiConfigs/domain/WorkspaceConfig";
import {resolveConfigFormHref} from "~/workspaceConfig/configHref";
import {loadOrCreateWorkspaceConfig} from "~/workspaceConfig/configRuntime";

function formKeyFromFormUrl(formUrl: string, workspaceId: Uuid): string {
    const path = formUrl.replaceAll("$workspaceId", workspaceId);
    const prefix = `/w/${workspaceId}/ottopilot/`;
    if (path.startsWith(prefix) === true) {
        return path.slice(prefix.length).split("?")[0] ?? "";
    }
    if (path.startsWith(`/w/${workspaceId}/`) === true) {
        return path.slice(`/w/${workspaceId}/`.length).split("?")[0] ?? "";
    }
    return path.replace(/^\/+/, "").split("?")[0] ?? "";
}

function parseUuid(value: string): Uuid | null {
    const parsed = uuidZodValidator.safeParse(value);
    return parsed.success === true ? parsed.data : null;
}

function formModeFromFormKey(formKey: string): Form["mode"] | null {
    const lastSegment = formKey.split("/").pop() ?? "";
    if (lastSegment === "log") {
        return "log";
    }
    if (lastSegment === "schedule") {
        return "schedule";
    }
    if (lastSegment === "edit") {
        return "update";
    }
    if (lastSegment === "create") {
        return "create";
    }
    return null;
}

function findFormPageByTemplateAndMode(
    config: WorkspaceConfig,
    eventTemplateId: Uuid,
    mode: Form["mode"],
): FormPage | undefined {
    for (const form of Object.values(config.forms)) {
        if (form.targetEntity === "eventContainer") {
            continue;
        }
        if (form.eventTemplateId !== eventTemplateId || form.mode !== mode) {
            continue;
        }
        const page = findFormPageByFormId(config, form.id);
        if (page !== undefined) {
            return page;
        }
    }
    return undefined;
}

function resolveConfiguredFormPage(args: {
    workspaceId: Uuid;
    eventTemplateId: Uuid;
    formUrl: string;
    buttonKind: OttopilotAdditionalButtonKind;
}): FormPage {
    const config = loadOrCreateWorkspaceConfig(args.workspaceId);
    const formKey = formKeyFromFormUrl(args.formUrl, args.workspaceId);
    const asUuid = parseUuid(formKey);
    if (asUuid !== null) {
        const byFormId = findFormPageByFormId(config, asUuid);
        if (byFormId !== undefined) {
            return byFormId;
        }
        const page = config.pages[asUuid];
        if (page !== undefined && isFormPage(page) === true) {
            return page;
        }
    }

    const mode =
        args.buttonKind === OttopilotAdditionalButtonKind.log
            ? "log"
            : formModeFromFormKey(formKey);
    if (mode !== null) {
        const byTemplateMode = findFormPageByTemplateAndMode(config, args.eventTemplateId, mode);
        if (byTemplateMode !== undefined) {
            return byTemplateMode;
        }
    }

    throw new Error(`There is no form configured for ${formKey}`);
}

/** Builds a config FormPage href from additional-button `formUrl` (form key, not a live route). */
export function buildAdditionalButtonFormUrl(params: {
    formUrl: string;
    formUrlQueryParams: ReadonlyArray<OttopilotAdditionalButtonFormUrlQueryParam> | undefined;
    workspaceId: Uuid;
    eventId: Uuid;
    eventContainerId: Uuid;
    eventTemplateId: Uuid;
    buttonKind: OttopilotAdditionalButtonKind;
}): string {
    const formPage = resolveConfiguredFormPage({
        workspaceId: params.workspaceId,
        eventTemplateId: params.eventTemplateId,
        formUrl: params.formUrl,
        buttonKind: params.buttonKind,
    });
    const search: Record<string, unknown> = {};
    const keys = params.formUrlQueryParams ?? [];
    for (const key of keys) {
        if (key === "workspaceId") {
            search.workspaceId = params.workspaceId;
        } else if (key === "eventId") {
            search.eventId = params.eventId;
        } else if (key === "eventContainerId") {
            search.eventContainerId = params.eventContainerId;
        }
    }
    const href = resolveConfigFormHref({
        workspaceId: params.workspaceId,
        formId: formPage.formId,
        search,
    });
    if (href === null) {
        throw new Error(`There is no form configured for ${formPage.formId}`);
    }
    return href;
}
